import ReactECharts from 'echarts-for-react';
import { useIsNarrow } from '@/components/blog/useIsNarrow';
import { useIsDark } from '../../useIsDark';

/**
 * SpamFilterLayersChart — blog18, Figure 1: four independent filter gates.
 *
 * Chart-type-first: this is a SEQUENTIAL PIPELINE of independent checks, not a
 * volume funnel (we have no per-stage drop-off counts) and not a bar comparison
 * (the stages aren't measured on one shared numeric axis) → a custom-rendered
 * horizontal stage diagram: four gate blocks in series, connected by arrows,
 * each carrying its own sourced stat as a label. Failing any one gate can stop
 * a message regardless of the others, which is the point the shape must make.
 *
 * Data: raisy/projects/blog/assets/blog18/raisy_email_format_deliverability_data.json
 * Sources: Google sender guidelines, Google Workspace Blog, SpamAssassin docs,
 * DMARC adoption tracking — see stat/source fields below and the post's
 * Sources footer. No invented numbers (Rule 24).
 *
 * Opaque background; ONE shared stat per stage (no separate hover copy, Rule 21).
 */

const BRAND = {
  teal: '#0AB3BF',
  tealDark: '#087B8B',
  info: '#3B82F6',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  slate: '#334155',
  muted: '#64748B',
  surface: '#FFFFFF',
};

type Stage = {
  name: string;
  short: string;
  color: string;
  stat: string;
  detail: string;
  source: string;
};

const STAGES: Stage[] = [
  {
    name: 'Network / gateway',
    short: 'Gateway',
    color: BRAND.slate,
    stat: 'IP reputation + rate checked before content is read',
    detail: 'A brand-new sending domain is throttled here regardless of what the email says.',
    source: 'Layered spam-filter architecture (connection-level filtering)',
  },
  {
    name: 'Authentication',
    short: 'Auth',
    color: BRAND.info,
    stat: 'SPF + DKIM + DMARC required at 5,000+ msgs/day',
    detail: 'Spam-complaint rate must stay under 0.3% (target under 0.1%) or mitigation eligibility is lost.',
    source: 'Google, official Gmail sender guidelines (support.google.com/mail/answer/14229414)',
  },
  {
    name: 'Mailbox-provider ML',
    short: 'ML model',
    color: BRAND.teal,
    stat: '~15B messages/day scored on reputation + engagement',
    detail: 'Opens, replies, deletes-without-reading, and spam reports feed back into the model per recipient.',
    source: "Google Workspace Blog, \"An overview of Gmail's spam filters\"",
  },
  {
    name: 'Client-side rules',
    short: 'Client',
    color: BRAND.warning,
    stat: 'Trained per recipient, invisible to the sender',
    detail: 'Desktop Outlook and Apple Mail apply personal rules on top of whatever the provider already decided.',
    source: 'Client-side filtering documentation (Outlook/Apple Mail desktop)',
  },
];

export const SpamFilterLayersChart = ({
  height,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const narrow = useIsNarrow();
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;

  const C = isDark
    ? { bg: '#0F172A', axis: '#E2E8F0', muted: '#94A3B8', card: 'bg-slate-900', border: 'border-slate-800', caption: 'text-slate-400' }
    : { bg: '#FFFFFF', axis: '#334155', muted: '#64748B', card: 'bg-white', border: 'border-slate-200', caption: 'text-slate-500' };

  // Desktop height calibrated against the worst-case real inline column width
  // (~700-750px post body, 4 boxes at 21.5% each -> ~150px per box): the
  // longest stage's title+stat+detail wraps to ~228px of content at that
  // width, so the box itself needs real margin above that, not just enough
  // for a best-case wide (ChartZoom lightbox) render.
  const chartHeight = height ?? (narrow ? 560 : 400);

  // Layout: stacked vertically on phones (one gate per row, arrow pointing
  // down), side-by-side on desktop (one gate per column, arrow pointing right).
  // Coordinates are in a 0-100 x 0-100 virtual grid; custom series draws boxes
  // + arrows directly so label text never gets clipped or auto-rotated.
  const boxes = STAGES.map((stage, i) => {
    if (narrow) {
      const boxH = 20;
      const gap = 4.6;
      const y = i * (boxH + gap);
      return { x: 4, y, w: 92, h: boxH, stage, i };
    }
    const boxW = 21.5;
    const gap = 3;
    const x = i * (boxW + gap);
    return { x, y: 8, w: boxW, h: 70, stage, i };
  });

  const renderStage = (params: unknown, api: any) => {
    const idx = api.value(0) as number;
    const b = boxes[idx];
    const p1 = api.coord([b.x, b.y]);
    const p2 = api.coord([b.x + b.w, b.y + b.h]);
    const rectX = Math.min(p1[0], p2[0]);
    const rectY = Math.min(p1[1], p2[1]);
    const rectW = Math.abs(p2[0] - p1[0]);
    const rectH = Math.abs(p2[1] - p1[1]);

    // Wrap width must track the box's ACTUAL rendered pixel width, not a
    // hardcoded guess — a fixed character count only looked right in the
    // ChartZoom lightbox (forced to a wide 1100px container) and overlapped
    // in the real inline column, which is much narrower. Text padding is 14px
    // each side; ~0.56em average glyph width is a safe estimate for this
    // sans-serif at these sizes.
    const innerW = rectW - 28;
    const charsPerLine = (fontPx: number) => Math.max(8, Math.floor(innerW / (fontPx * 0.56)));

    // Stack each text block from the ACTUAL line count of the one before it —
    // a fixed y-offset per block only worked when every title happened to fit
    // one line; once the box narrows (real inline column vs. the ChartZoom
    // lightbox's forced-wide container) the title itself wraps to 2 lines and
    // a fixed offset drives the next block straight through it.
    const titleText = wrapText(`${idx + 1}. ${b.stage.name}`, charsPerLine(14));
    const statText = wrapText(b.stage.stat, charsPerLine(12.5));
    const detailText = wrapText(b.stage.detail, charsPerLine(11));

    const titleLines = titleText.split('\n').length;
    const statLines = statText.split('\n').length;

    const titleLineH = 17;
    const statLineH = 16;
    const detailLineH = 15;
    const blockGap = 10;

    const titleY = rectY + 20;
    const statY = titleY + titleLines * titleLineH + blockGap;
    const detailY = statY + statLines * statLineH + blockGap;

    const children: any[] = [
      {
        type: 'rect',
        shape: { x: rectX, y: rectY, width: rectW, height: rectH, r: 10 },
        style: { fill: b.stage.color, opacity: 0.12, stroke: b.stage.color, lineWidth: 2 },
      },
      {
        type: 'text',
        style: {
          x: rectX + 14,
          y: titleY,
          text: titleText,
          fill: C.axis,
          font: 'bold 14px sans-serif',
          textAlign: 'left',
          lineHeight: titleLineH,
        },
      },
      {
        type: 'text',
        style: {
          x: rectX + 14,
          y: statY,
          text: statText,
          fill: b.stage.color,
          font: 'bold 12.5px sans-serif',
          textAlign: 'left',
          lineHeight: statLineH,
        },
      },
      {
        type: 'text',
        style: {
          x: rectX + 14,
          y: detailY,
          text: detailText,
          fill: C.muted,
          font: '11px sans-serif',
          textAlign: 'left',
          lineHeight: detailLineH,
        },
      },
    ];

    // Arrow to next stage
    if (idx < boxes.length - 1) {
      if (narrow) {
        const ax = rectX + rectW / 2;
        const ay1 = rectY + rectH + 2;
        children.push({
          type: 'path',
          shape: { pathData: `M ${ax} ${ay1} L ${ax} ${ay1 + 14} L ${ax - 5} ${ay1 + 9} M ${ax} ${ay1 + 14} L ${ax + 5} ${ay1 + 9}` },
          style: { stroke: C.muted, lineWidth: 2, fill: 'none' },
        });
      } else {
        const ay = rectY + rectH / 2;
        const ax1 = rectX + rectW + 2;
        children.push({
          type: 'path',
          shape: { pathData: `M ${ax1} ${ay} L ${ax1 + 12} ${ay} M ${ax1 + 12} ${ay} L ${ax1 + 6} ${ay - 5} M ${ax1 + 12} ${ay} L ${ax1 + 6} ${ay + 5}` },
          style: { stroke: C.muted, lineWidth: 2, fill: 'none' },
        });
      }
    }

    return { type: 'group', children };
  };

  const option = {
    backgroundColor: C.bg,
    grid: { left: 0, right: 0, top: 0, bottom: 0 },
    xAxis: { min: 0, max: 100, show: false },
    yAxis: { min: 0, max: narrow ? boxes[boxes.length - 1].y + boxes[boxes.length - 1].h + 4 : 100, show: false, inverse: !narrow },
    tooltip: {
      trigger: 'item',
      backgroundColor: '#1E293B',
      borderColor: BRAND.slate,
      textStyle: { color: '#F8FAFC', fontSize: 12 },
      formatter: (p: any) => {
        const b = boxes[p.value[0]];
        return `<b>${b.stage.name}</b><br/>${b.stage.stat}<br/><span style="opacity:0.75">${b.stage.source}</span>`;
      },
    },
    series: [
      {
        type: 'custom',
        renderItem: renderStage,
        data: boxes.map((b) => ({ value: [b.i, 0] })),
        z: 2,
      },
    ],
  };

  return (
    <div className={`rounded-xl border ${C.border} ${C.card} p-3`}>
      <ReactECharts option={option} style={{ height: chartHeight, width: '100%' }} opts={{ renderer: 'svg' }} />
      <p className={`mt-2 text-xs ${C.caption}`}>
        Four independent gates run in sequence on every send; failing any one can stop a message regardless of the others. Sources: Google sender
        guidelines, Google Workspace Blog, client-side filter documentation — see the post's Sources footer for dates and links.
      </p>
    </div>
  );
};

/** Simple greedy word-wrap for canvas text (ECharts custom series draws raw text, no CSS wrapping). */
function wrapText(text: string, maxCharsPerLine: number): string {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    if ((line + ' ' + w).trim().length > maxCharsPerLine) {
      lines.push(line.trim());
      line = w;
    } else {
      line = (line + ' ' + w).trim();
    }
  }
  if (line) lines.push(line);
  return lines.join('\n');
}
